import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Package, Check, Loader2, CreditCard, CalendarClock, XCircle, Gift, Tag, FileText, ExternalLink, Building2, Globe } from 'lucide-react';
import { getModuleIcon, MODULE_LANGS, moduleText } from '@/lib/moduleCatalog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin/subscription`;
const STUDIOS_API = `${process.env.REACT_APP_BACKEND_URL}/api/studios`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const chf = (n) => 'CHF ' + (Number(n) || 0).toLocaleString('de-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });const d10 = (s) => (s ? String(s).slice(0, 10) : '');

export default function SubscriptionPage() {
  const [modules, setModules] = useState([]);
  const [sub, setSub] = useState(null);
  const [selected, setSelected] = useState([]);
  const [interval, setIntervalState] = useState('year');
  const [lang, setLang] = useState(() => localStorage.getItem('myplan_lang') || 'de');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [trialDays, setTrialDays] = useState(0);
  const [isNew, setIsNew] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [nextDue, setNextDue] = useState(null);
  const [promoInput, setPromoInput] = useState('');
  const [promo, setPromo] = useState(null);
  const [studioLic, setStudioLic] = useState(null);
  const [licBusy, setLicBusy] = useState(false);
  const [costPreview, setCostPreview] = useState(null);
  const [extraCount, setExtraCount] = useState(1);
  const [breakdown, setBreakdown] = useState(null);
  const [purchases, setPurchases] = useState([]);
  const [moduleRecs, setModuleRecs] = useState(null);
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(API, auth());
      setModules(r.data.modules || []);
      const s = r.data.subscription;
      setSub(s);
      setTrialDays(r.data.trial_days || 0);
      setIsNew(!!r.data.is_new);
      if (s?.module_keys) setSelected(s.module_keys);
      if (s?.interval) setIntervalState(s.interval);
      const inv = await axios.get(`${API}/invoices`, auth());
      setInvoices(inv.data.invoices || []);
      setNextDue(inv.data.next_due || null);
      try {
        const lic = await axios.get(`${STUDIOS_API}/license-info`, auth());
        setStudioLic(lic.data);
      } catch { /* license info optional */ }
      try {
        const bd = await axios.get(`${STUDIOS_API}/license-breakdown`, auth());
        setBreakdown(bd.data);
      } catch { /* breakdown optional */ }
      try {
        const pu = await axios.get(`${STUDIOS_API}/license-purchases`, auth());
        setPurchases(pu.data.purchases || []);
      } catch { /* purchases optional */ }
    } catch { toast.error('Abo-Daten konnten nicht geladen werden'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const loadCostPreview = async (count) => {
    try {
      const r = await axios.get(`${STUDIOS_API}/license-cost-preview?count=${count}`, auth());
      setCostPreview(r.data);
    } catch { /* optional */ }
  };
  useEffect(() => { if (studioLic) loadCostPreview(extraCount); /* eslint-disable-next-line */ }, [extraCount, studioLic]);

  useEffect(() => {
    let active = true;
    axios.get(`${STUDIOS_API}/module-recommendations`, auth())
      .then((r) => { if (active) setModuleRecs(r.data); })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  useEffect(() => {
    const status = params.get('status');
    const sessionId = params.get('session_id');
    if (status === 'success' && sessionId) {
      let tries = 0;
      const poll = async () => {
        tries += 1;
        try {
          const r = await axios.get(`${API}/status/${sessionId}`, auth());
          if (r.data.paid) { toast.success('Abo aktiviert – vielen Dank!'); await load(); navigate('/admin/subscription', { replace: true }); return; }
        } catch {}
        if (tries < 8) setTimeout(poll, 2000);
        else { toast.message('Zahlung wird noch verarbeitet…'); navigate('/admin/subscription', { replace: true }); }
      };
      poll();
    } else if (status === 'cancel') {
      toast.message('Vorgang abgebrochen');
      navigate('/admin/subscription', { replace: true });
    }

    const lic = params.get('studio_license');
    const licSession = params.get('session_id');
    if (lic === 'success' && licSession) {
      let t2 = 0;
      const pollLic = async () => {
        t2 += 1;
        try {
          const r = await axios.get(`${API}/studio-license/status/${licSession}`, auth());
          if (r.data.paid) { toast.success('Studios freigeschaltet – du kannst sie jetzt anlegen.'); await load(); navigate('/studios?new=1', { replace: true }); return; }
        } catch {}
        if (t2 < 8) setTimeout(pollLic, 2000);
        else { toast.message('Zahlung wird noch verarbeitet…'); navigate('/admin/subscription', { replace: true }); }
      };
      pollLic();
    } else if (lic === 'cancel') {
      toast.message('Freischaltung abgebrochen');
      navigate('/admin/subscription', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = (key) => setSelected((prev) => prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]);

  const baseTotal = useMemo(() => {
    const field = interval === 'year' ? 'yearly_chf' : 'monthly_chf';
    return modules.filter((m) => selected.includes(m.key)).reduce((s, m) => s + Number(m[field] || 0), 0);
  }, [modules, selected, interval]);

  const discounted = useMemo(() => {
    if (!promo) return baseTotal;
    if (promo.percent_off) return Math.max(0, baseTotal * (1 - promo.percent_off / 100));
    if (promo.amount_off_chf) return Math.max(0, baseTotal - promo.amount_off_chf);
    return baseTotal;
  }, [baseTotal, promo]);

  const yearlySaving = useMemo(() => {
    const sel = modules.filter((m) => selected.includes(m.key));
    const m12 = sel.reduce((s, m) => s + Number(m.monthly_chf || 0), 0) * 12;
    const y = sel.reduce((s, m) => s + Number(m.yearly_chf || 0), 0);
    return Math.max(0, m12 - y);
  }, [modules, selected]);

  const freeMonths = useMemo(() => {
    const sel = modules.filter((m) => selected.includes(m.key));
    const mo = sel.reduce((s, m) => s + Number(m.monthly_chf || 0), 0);
    if (mo <= 0) return 2;
    return Math.round((yearlySaving / mo) * 10) / 10;
  }, [modules, selected, yearlySaving]);

  const freeMonthsLabel = freeMonths === 1 ? '1 Monat gratis' : `${Number.isInteger(freeMonths) ? freeMonths : freeMonths.toFixed(1)} Monate gratis`;

  const applyPromo = async () => {
    if (!promoInput.trim()) return;
    try {
      const r = await axios.post(`${API}/validate-promo`, { code: promoInput.trim() }, auth());
      setPromo(r.data);
      toast.success(`Rabatt „${r.data.code}“ angewendet`);
    } catch (e) { setPromo(null); toast.error(e?.response?.data?.detail || 'Ungültiger Code'); }
  };

  const subscribe = async () => {
    if (selected.length === 0) { toast.message('Bitte mindestens ein Modul wählen'); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/checkout`, { module_keys: selected, interval, origin_url: window.location.origin, promo_code: promo?.code || '' }, auth());
      if (r.data.checkout_url) window.location.href = r.data.checkout_url;
    } catch (e) { toast.error(e?.response?.data?.detail || 'Abo konnte nicht gestartet werden'); setBusy(false); }
  };

  const buyStudioLicense = async () => {
    setLicBusy(true);
    try {
      const r = await axios.post(`${API}/studio-license/checkout`, { interval, origin_url: window.location.origin, quantity: extraCount }, auth());
      if (r.data.checkout_url) window.location.href = r.data.checkout_url;
    } catch (e) { toast.error(e?.response?.data?.detail || 'Freischaltung konnte nicht gestartet werden'); setLicBusy(false); }
  };

  const cancelSub = async () => {
    if (!window.confirm('Abo zum Periodenende kündigen?')) return;
    setBusy(true);
    try { await axios.post(`${API}/cancel`, {}, auth()); toast.success('Abo wird zum Periodenende gekündigt'); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Kündigung fehlgeschlagen'); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="p-8 text-gray-500" data-testid="subscription-loading">Lädt…</div>;

  const isActive = sub?.status === 'active' || sub?.status === 'trialing';
  const showTrialBanner = isNew && trialDays > 0 && (!sub || sub.status === 'none' || sub.status === 'pending');

  return (
    <div className="mx-auto max-w-4xl p-6 space-y-6" data-testid="subscription-page">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Software-Abo</h1>
        <p className="mt-1 text-sm text-gray-500">Module wählen und das Abo an die InvictaX Group bezahlen. Der Betrag wird wiederkehrend eingezogen.</p>
      </div>

      {showTrialBanner && (
        <div className="flex items-center gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800" data-testid="subscription-trial-banner">
          <Gift className="h-5 w-5 shrink-0" />
          <span>Die ersten <strong>{trialDays} Tage gratis</strong> – der erste Einzug erfolgt erst nach Ablauf der Testphase.</span>
        </div>
      )}

      {sub && sub.status !== 'none' && (
        <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm" data-testid="subscription-current">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'}`}>
                  {isActive ? <Check className="h-3 w-3" /> : <CalendarClock className="h-3 w-3" />}
                  {sub.status === 'trialing' ? 'Testphase' : isActive ? 'Aktiv' : sub.status === 'canceling' ? 'Kündigt zum Periodenende' : sub.status}
                </span>
                <span className="text-sm text-gray-500">{sub.interval === 'year' ? 'Jährlich' : 'Monatlich'}</span>
              </div>
              <div className="mt-2 text-2xl font-bold text-gray-900">{chf(sub.amount_chf)}<span className="text-sm font-normal text-gray-400"> / {sub.interval === 'year' ? 'Jahr' : 'Monat'}</span></div>
              {sub.status === 'trialing' && sub.trial_end && <div className="mt-1 text-xs text-emerald-600">Testphase bis {d10(sub.trial_end)} – danach erster Einzug</div>}
              {nextDue && <div className="mt-1 text-xs text-gray-400">Nächste Fälligkeit: {d10(nextDue)}</div>}
            </div>
            {isActive && !sub.cancel_at_period_end && (
              <button onClick={cancelSub} disabled={busy} className="inline-flex items-center gap-1.5 rounded-lg border border-rose-200 px-3 py-2 text-sm text-rose-600 hover:bg-rose-50 disabled:opacity-50" data-testid="subscription-cancel">
                <XCircle className="h-4 w-4" /> Kündigen
              </button>
            )}
          </div>
        </div>
      )}

      {studioLic && (
        <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm" data-testid="studio-license-card">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="flex items-center gap-2 text-base font-semibold text-gray-900"><Building2 className="h-4 w-4 text-gray-400" /> Studio-Lizenzen</h2>
              <p className="mt-1 text-sm text-gray-500">
                Pro Studio wird eine Lizenz benötigt. Für jedes weitere Studio schaltest du hier eine zusätzliche Lizenz frei.
              </p>
              <div className="mt-3 text-sm text-gray-700" data-testid="studio-license-usage">
                Genutzt <strong>{studioLic.used}</strong> von <strong>{studioLic.limit}</strong> Studio{studioLic.limit === 1 ? '' : 's'}
              </div>
              {(() => {
                const eff = interval === 'year' ? studioLic.yearly_chf : studioLic.monthly_chf;
                const base = interval === 'year' ? (studioLic.base_yearly_chf ?? eff) : (studioLic.base_monthly_chf ?? eff);
                const per = interval === 'year' ? 'Jahr' : 'Monat';
                const saving = Math.max(0, (Number(base) || 0) - (Number(eff) || 0));
                const discounted = saving > 0.001;
                const label = studioLic.override_mode === 'fixed' || studioLic.override_mode === 'percent'
                  ? 'Deine Sonderkondition' : (studioLic.tier_percent > 0 ? 'Mengenrabatt' : '');
                return (
                  <div className="mt-2" data-testid="studio-license-price">
                    <div className="text-sm text-gray-700">
                      Zusätzliches Studio:{' '}
                      {discounted && <span className="mr-1.5 text-gray-400 line-through">{chf(base)}</span>}
                      <span className="font-semibold text-gray-900">{chf(eff)}</span> / {per}
                    </div>
                    {discounted && (
                      <div className="mt-1 inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700" data-testid="studio-license-savings">
                        Du sparst {chf(saving)} / {per}
                        {label && <span className="text-emerald-600/70">· {label}</span>}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
            <button onClick={buyStudioLicense} disabled={licBusy} className="inline-flex shrink-0 items-center gap-2 rounded-xl bg-gray-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-black disabled:opacity-50" data-testid="studio-license-buy">
              {licBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Building2 className="h-4 w-4" />}
              {extraCount === 1 ? 'Zusätzliches Studio freischalten' : `${extraCount} Studios freischalten`}
            </button>
          </div>

          {costPreview && costPreview.items.length > 0 && (
            <div className="mt-4 border-t border-gray-100 pt-4" data-testid="studio-cost-preview">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="text-sm font-medium text-gray-700">Mehrere Standorte? Gesamtkosten-Vorschau</div>
                <div className="flex items-center gap-2" data-testid="studio-cost-stepper">
                  <button onClick={() => setExtraCount(Math.max(1, extraCount - 1))} className="flex h-7 w-7 items-center justify-center rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50" data-testid="studio-cost-minus">−</button>
                  <span className="w-20 text-center text-sm font-semibold text-gray-900" data-testid="studio-cost-count">{extraCount} Studio{extraCount === 1 ? '' : 's'}</span>
                  <button onClick={() => setExtraCount(Math.min(10, extraCount + 1))} className="flex h-7 w-7 items-center justify-center rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50" data-testid="studio-cost-plus">+</button>
                </div>
              </div>
              <div className="mt-2 flex items-baseline gap-2">
                <span className="text-lg font-bold text-gray-900" data-testid="studio-cost-total">{chf(interval === 'year' ? costPreview.total_yearly_chf : costPreview.total_monthly_chf)}</span>
                <span className="text-sm text-gray-500">/ {interval === 'year' ? 'Jahr' : 'Monat'} zusammen</span>
              </div>
              {(() => {
                const baseT = interval === 'year' ? costPreview.base_total_yearly_chf : costPreview.base_total_monthly_chf;
                const totT = interval === 'year' ? costPreview.total_yearly_chf : costPreview.total_monthly_chf;
                const pkgSave = Math.max(0, (Number(baseT) || 0) - (Number(totT) || 0));
                return pkgSave > 0.001 ? (
                  <div className="mt-2 flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-sm font-semibold text-white" data-testid="studio-pkg-saving">
                    <Gift className="h-4 w-4" /> Mengenrabatt: du sparst {chf(pkgSave)} / {interval === 'year' ? 'Jahr' : 'Monat'}
                  </div>
                ) : null;
              })()}
              <div className="mt-1 text-xs text-gray-400">
                {extraCount} zusätzliche{extraCount === 1 ? 's' : ''} Studio{extraCount === 1 ? '' : 's'} über dein aktuelles Kontingent hinaus{costPreview.items.some((i) => i.tier_percent > 0) ? ' · Mengenrabatt berücksichtigt' : ''}.
              </div>
            </div>
          )}

          {breakdown && breakdown.items.length > 0 && (
            <div className="mt-4 border-t border-gray-100 pt-4" data-testid="studio-license-breakdown">
              <div className="mb-2 text-sm font-medium text-gray-700">Deine Studio-Lizenzen ({breakdown.limit} aktiv)</div>
              <div className="space-y-1">
                {breakdown.items.map((it) => (
                  <div key={it.no} className="flex items-center justify-between text-sm" data-testid={`studio-license-row-${it.no}`}>
                    <span className="text-gray-600">Studio {it.no}{it.included ? ' (im Grundabo enthalten)' : ''}{it.tier_percent > 0 ? ` · −${it.tier_percent}%` : ''}</span>
                    <span className={`font-medium ${it.included ? 'text-gray-400' : 'text-gray-900'}`}>
                      {it.included ? 'inklusive' : `${chf(interval === 'year' ? it.yearly_chf : it.monthly_chf)} / ${interval === 'year' ? 'Jahr' : 'Monat'}`}
                    </span>
                  </div>
                ))}
              </div>
              <div className="mt-2 flex items-center justify-between border-t border-gray-100 pt-2 text-sm font-semibold text-gray-900">
                <span>Summe Lizenzkosten</span>
                <span data-testid="studio-license-breakdown-total">{chf(interval === 'year' ? breakdown.total_yearly_chf : breakdown.total_monthly_chf)} / {interval === 'year' ? 'Jahr' : 'Monat'}</span>
              </div>
            </div>
          )}
        </div>
      )}

      {studioLic && (
        <div className="rounded-2xl border border-indigo-200 bg-indigo-50/50 p-5" data-testid="studio-module-suggestion">
          <h2 className="flex items-center gap-2 text-base font-semibold text-gray-900"><Package className="h-4 w-4 text-indigo-500" /> Modul-Vorschlag für deine Studios</h2>
          {selected.length > 0 ? (
            <p className="mt-1 text-sm text-gray-600" data-testid="studio-module-suggestion-text">
              Dein aktuelles Modul-Paket ({modules.filter((m) => selected.includes(m.key)).map((m) => m.name).join(', ')}) gilt automatisch für alle – auch neue – Studios. Du entscheidest, ob du es beibehältst oder anpasst.
            </p>
          ) : (
            <p className="mt-1 text-sm text-gray-600" data-testid="studio-module-suggestion-text">
              Für deine neuen Studios ist noch kein Modul-Paket gebucht. Wähle unten die gewünschten Module aus – du entscheidest, welche Leistungen aktiv sein sollen.
            </p>
          )}
          <button onClick={() => document.getElementById('module-grid')?.scrollIntoView({ behavior: 'smooth' })} className="mt-3 inline-flex items-center gap-1.5 rounded-xl border border-indigo-300 bg-white px-4 py-2 text-sm font-medium text-indigo-700 hover:bg-indigo-50" data-testid="studio-module-suggestion-adjust">
            Module anpassen
          </button>

          {moduleRecs?.recommendations?.length > 0 && (
            <div className="mt-4 space-y-2" data-testid="studio-module-recommendations">
              <div className="text-xs font-semibold uppercase tracking-wide text-indigo-500">{moduleRecs.headline || 'Empfohlen für deinen Betrieb'}</div>
              {moduleRecs.recommendations.filter((r) => !selected.includes(r.key)).map((r) => (
                <div key={r.key} className="flex items-start justify-between gap-3 rounded-xl border border-indigo-100 bg-white p-3" data-testid={`studio-module-rec-${r.key}`}>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-gray-900">{r.name} <span className="text-gray-400">· {chf(interval === 'year' ? r.yearly_chf : r.monthly_chf)}/{interval === 'year' ? 'J' : 'Mt'}</span></div>
                    <div className="mt-0.5 text-xs text-gray-600">{r.reason}</div>
                  </div>
                  <button onClick={() => { toggle(r.key); document.getElementById('module-grid')?.scrollIntoView({ behavior: 'smooth' }); }} className="shrink-0 rounded-lg bg-indigo-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-400" data-testid={`studio-module-rec-add-${r.key}`}>
                    Hinzufügen
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {purchases.length > 0 && (
        <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm" data-testid="studio-license-purchases">
          <h2 className="mb-3 flex items-center gap-2 text-base font-semibold text-gray-900"><Building2 className="h-4 w-4 text-gray-400" /> Paketkauf-Verlauf</h2>
          <table className="w-full text-sm">
            <thead><tr className="border-b border-gray-100 text-left text-xs uppercase text-gray-400"><th className="pb-2">Datum</th><th className="pb-2">Studios</th><th className="pb-2">Intervall</th><th className="pb-2 text-right">Betrag</th></tr></thead>
            <tbody className="divide-y divide-gray-50">
              {purchases.map((p, idx) => (
                <tr key={p.session_id || idx} data-testid={`studio-license-purchase-${idx}`}>
                  <td className="py-2 text-gray-600">{p.created_at ? new Date(p.created_at).toLocaleString('de-CH') : '—'}</td>
                  <td className="py-2 text-gray-800">+{p.quantity || 1}</td>
                  <td className="py-2 text-gray-500">{p.interval === 'year' ? 'jährlich' : 'monatlich'}</td>
                  <td className="py-2 text-right font-medium text-gray-800">{chf(p.amount_chf)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3" data-testid="subscription-interval-row">
        <div className="flex items-center gap-2 rounded-xl border border-gray-200 bg-white p-1 w-max" data-testid="subscription-interval">
          <button onClick={() => setIntervalState('month')} className={`rounded-lg px-4 py-2 text-sm font-medium ${interval === 'month' ? 'bg-blue-600 text-white' : 'text-gray-600'}`} data-testid="interval-month">Monatlich</button>
          <button onClick={() => setIntervalState('year')} className={`relative rounded-lg px-4 py-2 text-sm font-medium ${interval === 'year' ? 'bg-blue-600 text-white' : 'text-gray-600'}`} data-testid="interval-year">
            Jährlich
            {yearlySaving > 0.001 && (
              <span className={`ml-2 rounded-full px-2 py-0.5 text-xs font-semibold ${interval === 'year' ? 'bg-white/20 text-white' : 'bg-emerald-100 text-emerald-700'}`} data-testid="interval-year-badge">
                −{chf(yearlySaving)}/Jahr
              </span>
            )}
          </button>
        </div>
        {freeMonths >= 0.5 && (
          <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-700" data-testid="yearly-free-months">
            <Gift className="h-3.5 w-3.5" /> Jahresabo = {freeMonthsLabel}
          </span>
        )}
        {yearlySaving > 0.001 && interval !== 'year' && (
          <button onClick={() => setIntervalState('year')} className="text-sm font-medium text-emerald-700 hover:underline" data-testid="yearly-switch-hint">
            Jährlich zahlen und {chf(yearlySaving)} pro Jahr sparen
          </button>
        )}
      </div>

      <div className="flex items-center gap-1 rounded-xl border border-gray-200 bg-white p-1 w-max" data-testid="subscription-lang">
        <Globe className="ml-1 h-4 w-4 text-gray-400" />
        {MODULE_LANGS.map((l) => (
          <button key={l.code} onClick={() => setLang(l.code)} className={`rounded-lg px-3 py-1.5 text-xs font-semibold ${lang === l.code ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-100'}`} data-testid={`subscription-lang-${l.code}`}>{l.label}</button>
        ))}
      </div>

      <div id="module-grid" className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {modules.map((m) => {
          const on = selected.includes(m.key);
          const price = interval === 'year' ? m.yearly_chf : m.monthly_chf;
          const Ico = getModuleIcon(m.icon);
          const t = moduleText(m, lang);
          return (
            <button key={m.key} onClick={() => toggle(m.key)} data-testid={`module-card-${m.key}`}
              className={`flex items-start justify-between rounded-2xl border p-4 text-left transition-colors ${on ? 'border-blue-500 bg-blue-50/60' : 'border-gray-200 bg-white hover:border-gray-300'}`}>
              <div className="flex items-start gap-3">
                <div className={`mt-0.5 flex h-5 w-5 items-center justify-center rounded border ${on ? 'border-blue-600 bg-blue-600 text-white' : 'border-gray-300'}`}>{on && <Check className="h-3.5 w-3.5" />}</div>
                <div>
                  <div className="flex items-center gap-1.5 text-sm font-semibold text-gray-900"><Ico className="h-4 w-4 text-indigo-500" /> {m.name}</div>
                  <div className="mt-0.5 text-xs text-gray-500">{t.description}</div>
                  {t.benefits.length > 0 && (
                    <ul className="mt-2 space-y-1" data-testid={`subscription-benefits-${m.key}`}>
                      {t.benefits.map((b, i) => (
                        <li key={i} className="flex items-start gap-1.5 text-xs text-gray-700">
                          <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-500" />
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
              <div className="whitespace-nowrap text-sm font-medium text-gray-700">{chf(price)}</div>
            </button>
          );
        })}
      </div>

      {/* Rabatt-Code */}
      <div className="flex items-center gap-2" data-testid="subscription-promo">
        <Tag className="h-4 w-4 text-gray-400" />
        <input value={promoInput} onChange={(e) => setPromoInput(e.target.value)} placeholder="Rabatt-Code" className="rounded-lg border border-gray-300 px-3 py-2 text-sm uppercase" data-testid="subscription-promo-input" />
        <button onClick={applyPromo} className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50" data-testid="subscription-promo-apply">Anwenden</button>
        {promo && <span className="text-sm font-medium text-emerald-600" data-testid="subscription-promo-applied">−{promo.percent_off ? `${promo.percent_off}%` : chf(promo.amount_off_chf)}</span>}
      </div>

      {/* Summe + Abschluss */}
      <div className="sticky bottom-4 flex items-center justify-between rounded-2xl border border-gray-200 bg-white p-5 shadow-lg">
        <div>
          <div className="text-xs text-gray-500">Gesamt ({selected.length} Module, {interval === 'year' ? 'jährlich' : 'monatlich'})</div>
          <div className="flex items-baseline gap-2">
            {promo && baseTotal !== discounted && <span className="text-sm text-gray-400 line-through">{chf(baseTotal)}</span>}
            <span className="text-2xl font-bold text-gray-900" data-testid="subscription-total">{chf(discounted)}</span>
          </div>
          {interval === 'year' && yearlySaving > 0.001 && (
            <div className="mt-1 inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700" data-testid="subscription-yearly-saving">
              Du sparst {chf(yearlySaving)} pro Jahr{freeMonths >= 0.5 ? ` · ${freeMonthsLabel}` : ''}
            </div>
          )}
        </div>
        <button onClick={subscribe} disabled={busy || selected.length === 0} className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-6 py-3 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50" data-testid="subscription-subscribe">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
          {isActive ? 'Abo ändern' : showTrialBanner ? 'Gratis testen' : 'Abo abschliessen'}
        </button>
      </div>

      {/* Rechnungsverlauf */}
      {invoices.length > 0 && (
        <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm" data-testid="subscription-invoices">
          <h2 className="mb-3 flex items-center gap-2 text-base font-semibold text-gray-900"><FileText className="h-4 w-4 text-gray-400" /> Rechnungsverlauf</h2>
          <table className="w-full text-sm">
            <thead><tr className="border-b border-gray-100 text-left text-xs uppercase text-gray-400"><th className="pb-2">Datum</th><th className="pb-2">Nr.</th><th className="pb-2">Status</th><th className="pb-2 text-right">Betrag</th><th className="pb-2"></th></tr></thead>
            <tbody className="divide-y divide-gray-50">
              {invoices.map((i, idx) => (
                <tr key={i.number || idx} data-testid={`subscription-invoice-${idx}`}>
                  <td className="py-2 text-gray-600">{d10(i.date)}</td>
                  <td className="py-2 text-gray-500">{i.number || '—'}</td>
                  <td className="py-2"><span className={`rounded-full px-2 py-0.5 text-xs ${i.status === 'paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'}`}>{i.status}</span></td>
                  <td className="py-2 text-right font-medium text-gray-800">{chf(i.amount_chf)}</td>
                  <td className="py-2 text-right">{i.url && <a href={i.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline" data-testid={`subscription-invoice-link-${idx}`}>Ansehen <ExternalLink className="h-3 w-3" /></a>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
